import React, { createContext, useState, useContext, useEffect } from 'react';

const BookCartContext = createContext();

export const useBookCart = () => {
    const context = useContext(BookCartContext);
    if (!context) {
        throw new Error('useBookCart must be used within BookCartProvider');
    }
    return context;
};

// Deliberately separate from the old cart's 'cart' key so the two never collide.
const STORAGE_KEY = 'bookCart';

export const BookCartProvider = ({ children }) => {
    // Lazy-loaded from localStorage so the correct value is present on the very
    // first render — avoids a mount-order race with the save effect below
    // (which, under StrictMode's double-invoke, can otherwise persist an
    // empty array before this ever reads the real saved cart).
    const [items, setItems] = useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            return saved ? JSON.parse(saved) : [];
        } catch (error) {
            console.error('Error loading book cart:', error);
            return [];
        }
    });

    // Save on every change
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }, [items]);

    // Digital PDFs — no quantity. Returns 'added' or 'exists' so callers can
    // show the right feedback message.
    const addBook = (book) => {
        const bookId = book._id;
        const alreadyInCart = items.some((item) => item.bookId === bookId);
        if (alreadyInCart) {
            return 'exists';
        }

        const newItem = {
            bookId,
            title: book.title,
            subject: book.subject,
            thumbnail: book.thumbnail || '',
            price: book.discountPrice ?? book.price,
            originalPrice: book.price,
            university: book.university || null,
            session: book.session || null,
            semester: book.semester || null
        };

        setItems((prev) => [...prev, newItem]);
        return 'added';
    };

    const removeBook = (bookId) => {
        setItems((prev) => prev.filter((item) => item.bookId !== bookId));
    };

    const clearCart = () => {
        setItems([]);
    };

    const count = items.length;
    const total = items.reduce((sum, item) => sum + (item.price || 0), 0);

    const value = {
        items,
        count,
        total,
        addBook,
        removeBook,
        clearCart
    };

    return <BookCartContext.Provider value={value}>{children}</BookCartContext.Provider>;
};
